// Which content types an uploaded file may carry (#2068).
//
// A file opened from the app becomes a document at the app's own origin: the
// document library and assignment evidence open it as a blob: URL, and
// FileField opens it through the protected-file service worker at /download/.
// Neither path gets the api's `nosniff` / `CSP: sandbox` headers, so a file
// stored as image/svg+xml or text/html would run its script with the viewer's
// Firebase session. Anything outside this list is stored and served as
// application/octet-stream, which the browser downloads instead of rendering.
//
// Keep ACTIVE-free: nothing here may be a type a browser executes (HTML, SVG,
// XML, XHTML, JavaScript, MHTML). The protected-file service workers carry a
// copy of this list (they can't import); protectedFileWorker.test.js pins it.

export const SAFE_CONTENT_TYPES = new Set([
    'image/jpeg',
    'image/jpg',
    'image/pjpeg',
    'image/png',
    'image/gif',
    'image/webp',
    'image/bmp',
    'image/tiff',
    'image/heic',
    'image/heif',
    'image/avif',
    'application/pdf',
    'text/plain',
    'text/csv',
    'application/rtf',
    'text/rtf',
    'application/zip',
    'application/x-zip-compressed',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.template',
    'application/vnd.ms-word.document.macroEnabled.12',
    'application/vnd.ms-word.template.macroEnabled.12',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'application/vnd.openxmlformats-officedocument.presentationml.template',
    'application/vnd.openxmlformats-officedocument.presentationml.slideshow',
    'application/vnd.ms-powerpoint.presentation.macroEnabled.12',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.template',
    'application/vnd.ms-excel.sheet.macroEnabled.12',
    'application/vnd.oasis.opendocument.text',
    'application/vnd.oasis.opendocument.spreadsheet',
    'application/vnd.oasis.opendocument.presentation'
]);

// Media types compare case-insensitively; map back to the listed spelling.
const CANONICAL = new Map([...SAFE_CONTENT_TYPES].map(type => [type.toLowerCase(), type]));

// Audio and video have no scriptable subtypes, so the whole families pass.
const SAFE_FAMILIES = ['audio/', 'video/'];

export const DOWNLOAD_ONLY_CONTENT_TYPE = 'application/octet-stream';

// Extensions a browser treats as an active document even under a harmless
// declared type. The declared type is client-supplied, so an upload named
// with one of these is download-only whatever it claims to be.
const ACTIVE_EXTENSIONS = new Set([
    'htm', 'html', 'xht', 'xhtml', 'shtml', 'mht', 'mhtml',
    'svg', 'svgz', 'xml', 'xsl', 'xslt',
    'js', 'mjs'
]);

// Programs and scripts the OS runs on open. These are refused outright rather
// than stored download-only: nobody needs to share them through a records app,
// and a download from a trusted department site is exactly how they'd spread.
const EXECUTABLE_EXTENSIONS = new Set([
    'exe', 'msi', 'msp', 'msix', 'msixbundle', 'appx', 'appxbundle',
    'com', 'scr', 'pif', 'cpl', 'dll', 'sys', 'drv', 'ocx',
    'bat', 'cmd', 'ps1', 'psm1', 'psd1', 'vbs', 'vbe', 'js', 'jse', 'wsf', 'wsh', 'hta',
    'lnk', 'reg', 'inf', 'jar', 'application', 'gadget', 'msc',
    'sh', 'bash', 'zsh', 'csh', 'command', 'tool', 'app', 'dmg', 'pkg', 'mpkg',
    'apk', 'aab', 'ipa', 'deb', 'rpm'
]);

// The media type without parameters, lower-cased: 'Text/Plain; charset=x' -> 'text/plain'.
export const contentTypeEssence = type => (typeof type === 'string' ? type.split(';')[0].trim().toLowerCase() : '');

export const fileExtension = name => {
    const match = /\.([a-z0-9]+)$/i.exec(typeof name === 'string' ? name.trim() : '');
    return match ? match[1].toLowerCase() : '';
};

const canonicalSafeType = type => {
    const essence = contentTypeEssence(type);
    if (CANONICAL.has(essence)) return CANONICAL.get(essence);
    return SAFE_FAMILIES.some(family => essence.startsWith(family) && essence.length > family.length) ? essence : null;
};

export const isSafeContentType = type => canonicalSafeType(type) !== null;

// The type to store or serve: the declared type when it's passive, otherwise
// download-only. Pass the file name when there is one, so an active extension
// can't ride in under a passive declared type.
export const safeContentType = (type, name) => {
    if (name && ACTIVE_EXTENSIONS.has(fileExtension(name))) return DOWNLOAD_ONLY_CONTENT_TYPE;
    return canonicalSafeType(type) || DOWNLOAD_ONLY_CONTENT_TYPE;
};

export const isSvgFile = (type, name) => contentTypeEssence(type) === 'image/svg+xml' || ['svg', 'svgz'].includes(fileExtension(name));

export const isExecutableFileName = name => EXECUTABLE_EXTENSIONS.has(fileExtension(name));

// Re-label a fetched blob before it becomes a blob: URL. A blob: document
// inherits the app's origin and none of the response's headers, so the blob's
// own type is the only thing that decides whether it renders or downloads.
export const safeBlob = blob => {
    const type = safeContentType(blob?.type);
    return blob?.type === type ? blob : new Blob([blob], {type});
};
